import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import emailjs from '@emailjs/browser';
import '../styles/Contact.css';

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const socialRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: 'ERPNext & Frappe Customization',
    message: ''
  });

  const [formStatus, setFormStatus] = useState('');

  useEffect(() => {
    const handleServiceSelect = (e) => {
      if (e.detail) {
        setFormData((prev) => ({
          ...prev,
          service: e.detail,
          message: prev.message || `Hi Momen, I am interested in your ${e.detail} services.`
        }));
      }
    };

    window.addEventListener('select-service', handleServiceSelect);
    return () => window.removeEventListener('select-service', handleServiceSelect);
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const section = sectionRef.current;
      const form = formRef.current;
      const social = socialRef.current;

      gsap.from('.contact-title', {
        scrollTrigger: {
          trigger: section,
          start: 'top 75%',
          toggleActions: 'play none none none'
        },
        y: 60,
        scale: 0.95,
        duration: 1,
        ease: 'power3.out',
        clearProps: 'transform,opacity'
      });

      if (form) {
        gsap.from(form, {
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            toggleActions: 'play none none none'
          },
          y: 100,
          scale: 0.95,
          duration: 0.9,
          ease: 'power3.out',
          clearProps: 'transform,opacity'
        });
      }

      if (social) {
        gsap.from(social.children, {
          scrollTrigger: {
            trigger: social,
            start: 'top 85%',
            toggleActions: 'play none none none'
          },
          y: 40,
          scale: 0.9,
          stagger: 0.12,
          duration: 0.7,
          ease: 'power3.out',
          clearProps: 'transform,opacity'
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    setFormStatus('sending');
    
    // EmailJS configuration from environment variables
    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      service_type: formData.service,
      message: `[Service Requested: ${formData.service}]\n\n${formData.message}`,
      to_name: 'Momen Medhat',
    };
    
    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('Email sent successfully!', response.status, response.text);
        setFormStatus('success');
        setFormData({ name: '', email: '', service: 'ERPNext & Frappe Customization', message: '' });
        
        setTimeout(() => {
          setFormStatus('');
        }, 4000);
      })
      .catch((error) => {
        console.error('Failed to send email:', error);
        setFormStatus('error');
        
        setTimeout(() => {
          setFormStatus('');
        }, 4000);
      });
  };

  return (
    <section ref={sectionRef} className="contact" id="contact">
      <div className="contact-container">
        <h2 className="contact-title">Get In Touch</h2>
        <p className="contact-subtitle">
          Have an ERP project, integration challenge, or full-stack application in mind? Let's discuss how we can build it.
        </p>

        {/* Direct Contact Bar */}
        <div className="contact-quick-bar">
          <div className="contact-quick-header">
            <span className="quick-status-text">Direct Contact</span>
          </div>

          <div className="contact-quick-actions">
            <a
              href="https://wa.me/@kingmomen?text=Hi%20Momen,%20I%20saw%20your%20portfolio%20and%20would%20like%20to%20discuss%20a%20project!"
              target="_blank"
              rel="noopener noreferrer"
              className="quick-action-btn quick-whatsapp"
              id="quick-whatsapp-cta"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.711 2.598 2.664-.698c.972.531 1.77.813 2.796.813 3.179 0 5.767-2.587 5.767-5.766.001-3.181-2.587-5.767-5.767-5.767zm3.389 8.204c-.144.405-.837.774-1.17.824-.311.047-.709.073-2.146-.521-1.614-.668-2.671-2.302-2.753-2.411-.08-.109-.648-.864-.648-1.654 0-.79.414-1.18.561-1.341.144-.162.315-.202.422-.202.107 0 .214.002.308.007.1.006.233-.038.365.279.135.324.464 1.134.505 1.218.041.083.069.181.014.29-.054.11-.082.179-.164.275-.083.097-.174.216-.248.29-.083.082-.169.172-.073.337.097.165.433.714.93 1.156.638.569 1.176.746 1.341.828.166.082.263.072.361-.041.099-.114.422-.491.535-.659.112-.167.225-.14.378-.083.153.057.97.457 1.137.54.167.083.278.124.32.193.041.069.041.398-.103.803z"/>
                <path d="M12 2C6.477 2 2 6.477 2 12c0 1.891.524 3.662 1.435 5.176L2 22l4.981-1.306A9.957 9.957 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18.2c-1.696 0-3.279-.472-4.632-1.289l-.332-.198-3.085.809.823-3.007-.217-.346A8.17 8.17 0 013.8 12c0-4.521 3.679-8.2 8.2-8.2 4.522 0 8.2 3.679 8.2 8.2 0 4.521-3.678 8.2-8.2 8.2z"/>
              </svg>
              <span>Chat on WhatsApp</span>
            </a>

            <a
              href="https://www.linkedin.com/messaging/compose/?recipient=momenmedhatsalem"
              target="_blank"
              rel="noopener noreferrer"
              className="quick-action-btn quick-linkedin"
              id="quick-linkedin-cta"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
              <span>Message on LinkedIn</span>
            </a>
          </div>

          <div className="contact-or-divider">
            <span className="or-line"></span>
            <span className="or-text">or send a message</span>
            <span className="or-line"></span>
          </div>
        </div>

        <div className="contact-content">
          <div ref={formRef} className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="service">Project Focus</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="contact-select"
                >
                  <option value="ERPNext & Frappe Customization">ERPNext & Frappe Customization</option>
                  <option value="System Integration & APIs (Shopify / Tax)">System Integration & APIs (Shopify / Tax)</option>
                  <option value="Full-Stack Web App (React / Django / Python)">Full-Stack Web App (React / Django / Python)</option>
                  <option value="ZATCA / Tax Compliance Engine">ZATCA / Tax Compliance Engine</option>
                  <option value="Other Consulting / Architecture">Other Consulting / Architecture</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me about your project, workflow goals, or timeline..."
                  rows="5"
                  required
                />
              </div>

              <button 
                type="submit" 
                className={`submit-button ${formStatus}`}
                disabled={formStatus === 'sending'}
              >
                {formStatus === 'sending' ? 'Sending Message...' : 
                 formStatus === 'success' ? 'Message Sent Successfully! ✓' : 
                 formStatus === 'error' ? 'Failed to Send (Please use WhatsApp/Email) ✗' :
                 'Send Inquiry →'}
              </button>
            </form>
          </div>

          <div className="contact-info">
            {/* Email card */}
            <div className="info-item">
              <div className="info-icon">📧</div>
              <div className="info-text">
                <h3>Direct Email</h3>
                <a href="mailto:momensalemv@gmail.com">momensalemv@gmail.com</a>
              </div>
            </div>

            {/* Location card */}
            <div className="info-item">
              <div className="info-icon">📍</div>
              <div className="info-text">
                <h3>Location & Timezone</h3>
                <p>Cairo, Egypt (UTC+2 / UTC+3) · Available Worldwide</p>
              </div>
            </div>

            {/* Verified Social & Networking Links */}
            <div ref={socialRef} className="social-links">
              <a 
                href="https://www.linkedin.com/in/momenmedhatsalem/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link social-linkedin"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
                Connect on LinkedIn ↗
              </a>

              <a 
                href="https://github.com/momenmedhatsalem" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                Inspect GitHub Code ↗
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
